import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  QueryList
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { RadioButtonComponent } from './radio-button.component';

@Component({
  selector: 'axis-radio-button-wrapper',
  template: '<ng-content></ng-content>',
  host: {
    class: 'axis-radio-wrapper',
    role: 'radiogroup'
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioButtonWrapperComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RadioButtonWrapperComponent),
      multi: true
    }
  ]
})
export class RadioButtonWrapperComponent
  implements AfterContentInit, OnDestroy {
  @ContentChildren(RadioButtonComponent)
  radioComponents: QueryList<RadioButtonComponent>;
  @Input() selectedIndex = -1;
  @Input() selectedItem: Object;
  @Input() required = false;
  @Input() retrieveValue = false; // if true binds value only to ngModel
  @Input() selectedValue: string | number | boolean = '';
  @Input() rel: string;
  @Input() updateOn = 'blur';
  @Output() getSelectedItem: EventEmitter<any> = new EventEmitter<any>();
  @Output() getSelectedValue: EventEmitter<any> = new EventEmitter<any>();
  private model: any;
  private propagateModelChange = (_: any) => {};
  private OnTouchedEvent = () => {};

  constructor(public _changeDetRef: ChangeDetectorRef) {}

  ngAfterContentInit() {
    this.radioComponents
      .toArray()
      .forEach((comp: RadioButtonComponent, index) => {
        comp.changeDetect.subscribe(() => {
          this.resetWithIndex(index);
          this.selectedIndex = index;
          this.selectedValue = comp.value;
          this.selectedItem = {
            name: comp.name || '',
            value: comp.value,
            selectedIndex: this.selectedIndex
          };
        if (this.retrieveValue) {
          this.selectedItem = comp.value;
        }
          this.model = this.selectedItem;
          this.propagateModelChange(this.model);
          this.getSelectedItem.emit(this.selectedItem);
          this.getSelectedValue.emit(this.selectedValue);
          if (this.updateOn === 'click') {
            this.OnTouchedEvent();
          }
        });
        comp.emitBlurEvent.subscribe(() => {
          this.OnTouchedEvent();
        });
      });
      // check if model value is defined or false (false is for pre-selected boolean value)
      if (this.model === false || this.model) {
        this.writeValue(this.model);
      }
  }

  resetWithIndex(item) {
    this.radioComponents
      .toArray()
      .forEach((comp: RadioButtonComponent, index) => {
        if (item !== index) {
          comp.checked = false;
        } else {
          comp.checked = true;
        }
      });
  }

  // Implementing ControlValueAccessor for RadioButtonWrapperComponent
  // ::For custom form validations(triggered when assigned to ngModel)
  writeValue(value: any) {
    if (value === false || value || value === 0) {
      this.model = value;
      this.preDefinedSelection(this.model);
    } else {
      this.reset();
    }
  }

  preDefinedSelection(model) {
    if (this.radioComponents) {
      this.radioComponents
        .toArray()
        .forEach((comp: RadioButtonComponent, index) => {
          let isMatched = false;

          if (typeof model === 'boolean' ) {
            if (model.toString() === comp.value || model === comp.value || model.toString() === comp.heading) {
              isMatched = true;
              this.updateModelValue(model, index);
            }
          } else if (typeof model === 'string') {
            if (model === comp.value || model === comp.heading) {
              isMatched = true;
              this.updateModelValue(model, index);
            }
          } else if (typeof model === 'number') {
            if (model === index) {
              isMatched = true;
              this.updateModelValue(comp.value, index);
            }
          } else {
            if (comp.value === model.value) {
              isMatched = true;
              this.updateModelValue(comp.value, index);
            }
          }

          if (isMatched) {
            comp.checked = true;
            this.selectedItem = {
              name: comp.name || '',
              value: comp.value,
              selectedIndex: this.selectedIndex
            };
            this.model = this.selectedItem;
          } else {
            comp.checked = false;
          }
          if (!comp._changeDetRef['destroyed']) {
            comp._changeDetRef.detectChanges();
          }
        });
    }
    if (!this._changeDetRef['destroyed']) {
      this._changeDetRef.detectChanges();
    }
  }

  updateModelValue(value, index) {
    this.selectedValue = value;
    this.selectedIndex = index;
  }

  registerOnChange(fn: any): void {
    this.propagateModelChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.OnTouchedEvent = fn;
  }
  validate(control: AbstractControl): { [key: string]: any } {
    if (this.required) {
      if (control.value !== -1) {
        return null;
      }
      return { axisRadiobutton: { valid: false } };
    }
  }

  toggleDisable() {
    this.radioComponents.toArray().forEach((comp: RadioButtonComponent) => {
      comp.disabled = !comp.disabled;
    });
  }

  setDisabledState(isDisabled: boolean) {
    if (this.radioComponents) {
      this.radioComponents.toArray().forEach((comp: RadioButtonComponent) => {
        comp.disabled = isDisabled;
      });
    }
  }

  reset() {
    if (this.radioComponents) {
      this.radioComponents.toArray().forEach((comp: RadioButtonComponent) => {
        comp.checked = false;
      });
    }
    this.selectedIndex = -1;
    this.selectedValue = '';
    this.selectedItem = null;
    this.model = null;
  }

  ngOnDestroy() {
    this.radioComponents.toArray().forEach((comp: RadioButtonComponent) => {
      comp.changeDetect.unsubscribe();
      comp.emitBlurEvent.unsubscribe();
    });
  }
}
